import React, { useState } from 'react';
import DefaultFooter from "../components/default-footer";
import Header from "../components/header";
import WhatsAppButton from "../components/WhatsAppButton";
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

function LoginCustomer() {
    const navigate = useNavigate();

    // Form field state
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    // Error and loading state
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Input change handler
    const handleChange = (e: { target: { name: any; value: any; }; }) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Clear this field's error and the general error as soon as the user edits it
        if (fieldErrors[name as 'email' | 'password']) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[name as 'email' | 'password'];
                return next;
            });
        }
        if (error) setError('');
    };

    const handleBlur = (e: { target: { name: any; value: any; }; }) => {
        const { name, value } = e.target;
        setFieldErrors((prev) => {
            const next = { ...prev };
            if (name === 'email') {
                if (!value.trim()) {
                    next.email = 'Email address is required.';
                } else if (!EMAIL_RULE.test(value)) {
                    next.email = 'Enter a valid email address.';
                } else {
                    delete next.email;
                }
            }
            if (name === 'password') {
                if (!value) {
                    next.password = 'Password is required.';
                } else {
                    delete next.password;
                }
            }
            return next;
        });
    };

    const validate = () => {
        const next: { email?: string; password?: string } = {};
        if (!formData.email.trim()) {
            next.email = 'Email address is required.';
        } else if (!EMAIL_RULE.test(formData.email)) {
            next.email = 'Enter a valid email address.';
        }
        if (!formData.password) {
            next.password = 'Password is required.';
        }
        return next;
    };

    // Form submit handler
    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        setError('');

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Incorrect email or password.');
            }

            // Store the token and user info
            localStorage.setItem('token_customer_silverjarz', data.access_token);
            localStorage.setItem('customer_silverjarz', JSON.stringify(data.user));

            // Redirect based on role
            if (data.user.role === 'customer') {
                navigate('/my-dashboard');
            } else if (data.user.role === 'admin') {
                navigate('/admin-dashboard');
            } else {
                navigate('/dashboard');
            }

        } catch (err: any) {
            setError(err.message || 'Something went wrong while signing in. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setError('');
            const response = await fetch(`${API_URL}/auth/google/redirect`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                }
            });
            const data = await response.json();
            if (data.url) {
                window.location.href = data.url; // Redirect to Google's sign-in screen
            } else {
                throw new Error('Unable to sign in with Google.');
            }
        } catch (err) {
            setError('Something went wrong redirecting to Google. Please try again.');
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

                :root {
                    --p: #294b5a;
                    --p-dark: #1a313d;
                    --p-light: #3a6275;
                    --gold: #d3be76;
                    --gold-lt: #ede4b5;
                    --gold-dk: #b8a258;
                    --cream: #f7f5f1;
                    --muted: #7a8a91;
                    --white: #ffffff;
                    --error: #dc2626;
                    --error-light: #fee2e2;
                    --success: #10b981;
                    --border-dark: #cbd5e0;
                    --border-darker: #94a3b8;
                    --border-focus: #d3be76;
                }

                .login-page {
                    font-family: 'DM Sans', sans-serif;
                    background: linear-gradient(135deg, var(--cream) 0%, #ffffff 100%);
                    min-height: 100vh;
                }

                /* ─── FORM ELEGANCE ─── */
                .corp-eyebrow {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 10px;
                    font-weight: 600;
                    color: var(--gold-dk);
                    margin-bottom: 10px;
                }
                .corp-eyebrow::before {
                    content: '';
                    display: block;
                    width: 24px;
                    height: 2px;
                    background: var(--gold);
                    border-radius: 2px;
                }

                .corp-section-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: var(--p-dark);
                    line-height: 1.15;
                    margin: 0 0 12px;
                }
                .corp-section-title em { 
                    font-style: italic; 
                    color: var(--p-light);
                }

                .login-card {
                    background: var(--white);
                    border-radius: 16px;
                    box-shadow: 0 25px 50px -12px rgba(41, 75, 90, 0.25);
                    overflow: hidden;
                    border: 1px solid rgba(41, 75, 90, 0.08);
                }

                .login-header {
                    padding: 40px 40px 20px;
                    text-align: center;
                    border-bottom: 1px solid rgba(41, 75, 90, 0.06);
                }

                .logo-wrapper {
                    display: inline-flex;
                    padding: 12px 24px;
                    background: var(--cream);
                    border-radius: 100px;
                    margin-bottom: 20px;
                    border: 1px solid rgba(211, 190, 118, 0.2);
                }

                .login-form {
                    padding: 30px 40px 40px;
                }

                /* ─── ERROR MESSAGE ─── */
                .error-message {
                    background-color: var(--error-light);
                    border: 2px solid var(--error);
                    color: var(--error);
                    padding: 12px 16px;
                    border-radius: 12px;
                    margin-bottom: 20px;
                    font-size: 13px;
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                }
                .error-message svg {
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                /* ─── FORM FIELDS ─── */
                .corp-input-group {
                    margin-bottom: 24px;
                }

                .corp-input-label {
                    display: block;
                    font-size: 12px;
                    font-weight: 600;
                    color: var(--p);
                    margin-bottom: 8px;
                }

                .corp-input-wrapper {
                    position: relative;
                }

                .corp-input-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    display: flex;
                    color: var(--p);
                    opacity: 0.9;
                    z-index: 1;
                    pointer-events: none;
                }

                .password-toggle {
                    position: absolute;
                    right: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    padding: 4px;
                    cursor: pointer;
                    color: var(--p);
                    opacity: 0.7;
                    display: flex;
                    z-index: 1;
                }
                .password-toggle:hover {
                    opacity: 1;
                }

                .corp-input {
                    width: 100%;
                    height: 54px;
                    padding: 0 16px 0 48px !important;
                    border: 3px solid #94a3b8 !important;
                    border-radius: 12px !important;
                    font-family: 'DM Sans', sans-serif;
                    font-size: 15px;
                    color: var(--p-dark);
                    background: var(--white) !important;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
                    box-sizing: border-box;
                }
                .corp-input:hover {
                    border-color: #6b7f88 !important;
                    background-color: #fafafa !important;
                }
                .corp-input:focus {
                    outline: none !important;
                    border-color: var(--gold) !important;
                    border-width: 3px !important;
                    box-shadow: 0 0 0 4px rgba(211, 190, 118, 0.25) !important;
                    background-color: #ffffff !important;
                }
                .corp-input.error {
                    border-color: var(--error) !important;
                    background: var(--error-light) !important;
                }
                .corp-input::placeholder {
                    color: #64748b;
                    font-weight: 400;
                    opacity: 0.8;
                }
                .corp-input:disabled {
                    background: #f1f3f4 !important;
                    color: #94a3b8;
                    cursor: not-allowed;
                }

                .field-error {
                    margin: 6px 0 0;
                    font-size: 12px;
                    color: var(--error);
                }

                /* ─── FORM OPTIONS ─── */
                .form-options {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 28px;
                }

                .remember-me {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                }
                .remember-me input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                    border: 2px solid #94a3b8;
                    border-radius: 4px;
                    cursor: pointer;
                    accent-color: var(--gold);
                }
                .remember-me span {
                    font-size: 13px;
                    color: var(--p-light);
                }

                .forgot-password {
                    font-size: 13px;
                    color: var(--gold-dk);
                    text-decoration: none;
                    font-weight: 500;
                    transition: color 0.2s;
                }
                .forgot-password:hover {
                    color: var(--p);
                }

                /* ─── BUTTONS ─── */
                .corp-btn-primary {
                    width: 100%;
                    height: 54px;
                    background: var(--p);
                    color: var(--white);
                    border: none;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    position: relative;
                }
                .corp-btn-primary:hover:not(:disabled) {
                    background: var(--p-dark);
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px -5px rgba(41, 75, 90, 0.4);
                }
                .corp-btn-primary:active:not(:disabled) {
                    transform: translateY(0);
                }
                .corp-btn-primary:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                /* ─── LOADING SPINNER ─── */
                .spinner {
                    width: 18px;
                    height: 18px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: spin 0.7s linear infinite;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* ─── DIVIDER ─── */
                .corp-divider {
                    position: relative;
                    text-align: center;
                    margin: 30px 0;
                }
                .corp-divider::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: linear-gradient(90deg, transparent, #94a3b8, #94a3b8, transparent);
                }
                .corp-divider span {
                    position: relative;
                    background: var(--white);
                    padding: 0 20px;
                    font-size: 12px;
                    color: #475569;
                    font-weight: 500;
                }

                /* ─── SOCIAL BUTTONS ─── */
                .social-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    margin-bottom: 30px;
                }

                .corp-btn-social {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    width: 100%;
                    height: 50px;
                    border: 2px solid #cbd5e1;
                    border-radius: 12px;
                    background: var(--white);
                    font-size: 14px;
                    font-weight: 500;
                    color: var(--p-dark);
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .corp-btn-social:hover:not(:disabled) {
                    border-color: var(--gold);
                    background: var(--cream);
                    transform: translateY(-2px);
                }
                .corp-btn-social:active:not(:disabled) {
                    transform: translateY(0);
                }
                .corp-btn-social.google:hover:not(:disabled) {
                    border-color: #DB4437;
                }
                .corp-btn-social.facebook:hover:not(:disabled) {
                    border-color: #4267B2;
                }

                /* ─── TERMS ─── */
                .terms-text {
                    text-align: center;
                    font-size: 11px;
                    color: #64748b;
                    line-height: 1.6;
                    margin-top: 20px;
                }
                .terms-text a {
                    color: var(--p);
                    text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .terms-text a:hover {
                    color: var(--gold-dk);
                }

                /* ─── REGISTER LINK ─── */
                .register-prompt {
                    text-align: center;
                    margin-top: 25px;
                    padding-top: 25px;
                    border-top: 2px solid #cbd5e1;
                }
                .register-prompt p {
                    font-size: 14px;
                    color: var(--p-light);
                }
                .register-link {
                    color: var(--gold-dk);
                    text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: rgba(211, 190, 118, 0.1);
                    padding: 6px 12px;
                    border-radius: 20px;
                }
                .register-link:hover {
                    color: var(--p);
                    background: rgba(41, 75, 90, 0.1);
                }

                /* ─── FEATURE LIST ─── */
                .feature-list {
                    margin-top: 30px;
                    padding: 20px;
                    background: var(--cream);
                    border-radius: 12px;
                    border: 2px solid #cbd5e1;
                }
                .feature-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 12px;
                    color: var(--p-light);
                    margin-bottom: 12px;
                }
                .feature-item:last-child {
                    margin-bottom: 0;
                }
                .feature-icon {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: var(--gold);
                    color: var(--p-dark);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: bold;
                    border: 1px solid var(--gold-dk);
                }

                /* ─── RESPONSIVE ─── */
                @media (max-width: 768px) {
                    .login-header {
                        padding: 30px 20px 15px;
                    }
                    .login-form {
                        padding: 20px 20px 30px;
                    }
                }
            `}</style>

            <div className="login-page">
                <Header />

                <section className="layout-pt-lg layout-pb-lg" style={{ background: 'linear-gradient(135deg, var(--cream) 0%, #ffffff 100%)' }}>
                    <div className="container">
                        <div className="row justify-center">
                            <div className="col-xl-5 col-lg-6 col-md-8">
                                <div className="login-card">
                                    {/* Header with Logo */}
                                    <div className="login-header">
                                        <div className="logo-wrapper">
                                            <img
                                                src="/img/logo-100.png"
                                                alt="Logo"
                                                style={{ height: '40px', width: 'auto' }}
                                            />
                                        </div>
                                        <span className="corp-eyebrow">Welcome Back</span>
                                        <h2 className="corp-section-title">
                                            Sign in to <em>Your Account</em>
                                        </h2>
                                    </div>

                                    {/* Login Form */}
                                    <div className="login-form">
                                        {/* Error message */}
                                        {error && (
                                            <div className="error-message">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <path d="M12 8v5M12 16h.01" />
                                                </svg>
                                                <span>{error}</span>
                                            </div>
                                        )}

                                        <form onSubmit={handleSubmit} noValidate>
                                            {/* Email Field */}
                                            <div className="corp-input-group">
                                                <label className="corp-input-label">Email Address</label>
                                                <div className="corp-input-wrapper">
                                                    <span className="corp-input-icon">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="2" y="4" width="20" height="16" rx="2" />
                                                            <path d="m22 6-10 7L2 6" />
                                                        </svg>
                                                    </span>
                                                    <input 
                                                        type="email" 
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={`corp-input ${fieldErrors.email ? 'error' : ''}`}
                                                        placeholder="your@email.com"
                                                        disabled={loading}
                                                    />
                                                </div>
                                                {fieldErrors.email && <p className="field-error">{fieldErrors.email}</p>}
                                            </div>

                                            {/* Password Field */}
                                            <div className="corp-input-group">
                                                <label className="corp-input-label">Password</label>
                                                <div className="corp-input-wrapper">
                                                    <span className="corp-input-icon">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="3" y="11" width="18" height="11" rx="2" />
                                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                        </svg>
                                                    </span>
                                                    <input 
                                                        type={showPassword ? "text" : "password"}
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={`corp-input ${fieldErrors.password ? 'error' : ''}`}
                                                        placeholder="••••••••"
                                                        disabled={loading}
                                                    />
                                                    <button 
                                                        type="button"
                                                        className="password-toggle"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        disabled={loading}
                                                        tabIndex={-1}
                                                    >
                                                        {showPassword ? (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                                                                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68M6.61 6.61A13.52 13.52 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                                                                <path d="m2 2 20 20" />
                                                            </svg>
                                                        ) : (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                                                                <circle cx="12" cy="12" r="3" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>
                                                {fieldErrors.password && <p className="field-error">{fieldErrors.password}</p>}
                                            </div>
                                            {/* Form Options */}
                                            <div className="form-options">
                                                <label className="remember-me">
                                                    <input type="checkbox" defaultChecked disabled={loading} />
                                                    <span>Remember me</span>
                                                </label>
                                                <a href="/forgot-password" className="forgot-password">
                                                    Forgot Password?
                                                </a>
                                            </div>

                                            {/* Submit Button */}
                                            <div>
                                                <button 
                                                    type="submit" 
                                                    className="corp-btn-primary"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner" />
                                                            <span>Signing in...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span>Sign In</span>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M5 12h14M12 5l7 7-7 7" />
                                                            </svg>
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            {/* Divider */}
                                            <div className="corp-divider">
                                                <span>Or continue with</span>
                                            </div>

                                            {/* Social Login */}
                                            <div className="social-buttons">
                                                <button
                                                    type="button"
                                                    className="corp-btn-social google"
                                                    onClick={handleGoogleLogin}
                                                    disabled={loading}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24">
                                                        <path fill="#DB4437" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                        <path fill="#4285F4" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                                    </svg>
                                                    <span>Sign in with Google</span>
                                                </button>
                                            </div>

                                            {/* Terms */}
                                            <div className="terms-text">
                                                By signing in, you agree to our{' '}
                                                <a href="/terms">Terms of Service</a> and{' '}
                                                <a href="/privacy">Privacy Statement</a>.
                                            </div>
                                        </form>

                                        {/* Register Link */}
                                        <div className="register-prompt">
                                            <p>
                                                Don't have an account?{' '}
                                                <a href="/sign-up" className="register-link">
                                                    Create free account
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                                    </svg>
                                                </a>
                                            </p>
                                        </div>

                                        {/* Feature List */}
                                        <div className="feature-list">
                                            <div className="feature-item">
                                                <span className="feature-icon">✓</span>
                                                <span>Access your dashboard and bookings</span>
                                            </div>
                                            <div className="feature-item">
                                                <span className="feature-icon">✓</span>
                                                <span>Manage your visa applications</span>
                                            </div>
                                            <div className="feature-item">
                                                <span className="feature-icon">✓</span>
                                                <span>Track application status in real-time</span>
                                            </div>
                                            <div className="feature-item">
                                                <span className="feature-icon">✓</span>
                                                <span>Save payment methods for faster checkout</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <DefaultFooter />
                <WhatsAppButton />
            </div>
        </>
    );
}

export default LoginCustomer;